import React, { useState, useRef, useEffect } from 'react';
import StockPanel from './components/StockPanel';
import OrderPanel from './components/OrderPanel';
import ResultsPanel from './components/ResultsPanel';
import HistoryPanel from './components/HistoryPanel';
import { PRIORITY_METRICS, DEFAULT_PRIORITY_ORDER } from './utils/packing';
import { saveRun, getHistory, deleteRun } from './utils/db';
import './App.css';

const DEFAULT_STOCK = [
  { w: 321, h: 250, qty: 100 },
  { w: 321, h: 225, qty: 100 },
];

const DEFAULT_ORDER = [
  { w: 49.5, h: 143.5, qty: 15 },
  { w: 105,  h: 160,   qty: 25 },
  { w: 95,   h: 220,   qty: 36 },
];

const PRIORITY_LABELS = ['1st', '2nd', '3rd'];
const ORDER_OPTIONS = [
  { value: 'standard', label: 'Standard (W/H +0 cm)',      delta: 0    },
  { value: 'plus',     label: 'Expanded (W/H +3.4 cm)',    delta: 3.4  },
  { value: 's-minus',  label: 'Reduced (W/H -0.6 cm)',     delta: -0.6 },
  { value: 'm-minus',  label: 'Reduced (W/H -1.1 cm)',     delta: -1.1 },
  { value: 'l-minus',  label: 'Reduced (W/H -5.1 cm)',     delta: -5.1 },
];

export default function App() {
  const [stock, setStock]               = useState(DEFAULT_STOCK);
  const [order, setOrder]               = useState(DEFAULT_ORDER);
  const [wasteW, setWasteW]             = useState(1);
  const [wasteH, setWasteH]             = useState(2);
  const [orderMode, setOrderMode]       = useState('standard');
  const [priorityOrder, setPriorityOrder] = useState(DEFAULT_PRIORITY_ORDER);
  const [result, setResult]             = useState(null);
  const [hasRun, setHasRun]             = useState(false);
  const [solving, setSolving]           = useState(false);
  const [progress, setProgress]         = useState(0);
  const [history, setHistory]           = useState([]);
  const [activeHistoryId, setActiveHistoryId] = useState(null);

  const workerRef      = useRef(null);
  const pendingModeRef = useRef(null);

  // ── Worker factory — reused on init and after cancel ─────────────────
  function spawnWorker() {
    const worker = new Worker(new URL('./utils/packing.worker.js', import.meta.url));
    worker.onmessage = ({ data }) => {
      if (data.type === 'progress') {
        setProgress(data.percent);
      } else if (data.type === 'done') {
        const mode = pendingModeRef.current;
        const fullResult = {
          ...data.result,
          orderMode:      mode.value,
          orderModeLabel: mode.label,
          orderDelta:     mode.delta,
        };
        setResult(fullResult);
        setSolving(false);
        setHasRun(true);
        saveRun({
          stock:    stockRef.current,
          order:    orderRef.current,
          settings: {
            wasteW:        wasteWRef.current,
            wasteH:        wasteHRef.current,
            orderMode:     orderModeRef.current,
            priorityOrder: priorityOrderRef.current,
          },
          result: fullResult,
        });
        setHistory(getHistory());
        setActiveHistoryId(null);
      }
    };
    return worker;
  }

  useEffect(() => {
    workerRef.current = spawnWorker();
    return () => workerRef.current.terminate();
  // eslint-disable-next-line
  }, []);

  function cancelOptimize() {
    workerRef.current.terminate();
    workerRef.current = spawnWorker();
    setSolving(false);
    setProgress(0);
  }

  // ── Stable refs so worker onmessage closure can read latest state ─────
  const stockRef         = useRef(stock);
  const orderRef         = useRef(order);
  const wasteWRef        = useRef(wasteW);
  const wasteHRef        = useRef(wasteH);
  const orderModeRef     = useRef(orderMode);
  const priorityOrderRef = useRef(priorityOrder);

  useEffect(() => { stockRef.current         = stock;         }, [stock]);
  useEffect(() => { orderRef.current         = order;         }, [order]);
  useEffect(() => { wasteWRef.current        = wasteW;        }, [wasteW]);
  useEffect(() => { wasteHRef.current        = wasteH;        }, [wasteH]);
  useEffect(() => { orderModeRef.current     = orderMode;     }, [orderMode]);
  useEffect(() => { priorityOrderRef.current = priorityOrder; }, [priorityOrder]);

  // ── Load history + restore last run on startup ────────────────────────
  useEffect(() => {
    const hist = getHistory();
    setHistory(hist);
    if (hist.length > 0) applyRun(hist[0], true);
  }, []);

  // ── Helpers ──────────────────────────────────────────────────────────────
  function applyRun(run, isRestore = false) {
    setStock(run.stock);
    setOrder(run.order);
    setWasteW(run.settings.wasteW);
    setWasteH(run.settings.wasteH);
    setOrderMode(run.settings.orderMode);
    setPriorityOrder(run.settings.priorityOrder);
    setResult(run.result);
    setHasRun(true);
    if (!isRestore) setActiveHistoryId(run.id);
  }

  function handleDeleteRun(id) {
    deleteRun(id);
    setHistory(getHistory());
    if (activeHistoryId === id) setActiveHistoryId(null);
  }

  function updatePriority(index, metric) {
    setPriorityOrder((current) => {
      const next = [...current];
      const existingIndex = next.indexOf(metric);
      if (existingIndex === -1) { next[index] = metric; return next; }
      [next[index], next[existingIndex]] = [next[existingIndex], next[index]];
      return next;
    });
  }

  function optimize() {
    if (stock.length === 0 || order.length === 0) return;
    const selectedMode = ORDER_OPTIONS.find((o) => o.value === orderMode) || ORDER_OPTIONS[0];
    const adjustedOrder = order.map((o) => ({
      ...o,
      w: Number((o.w + selectedMode.delta).toFixed(2)),
      h: Number((o.h + selectedMode.delta).toFixed(2)),
    }));
    if (adjustedOrder.some((o) => o.w <= 0 || o.h <= 0)) return;

    pendingModeRef.current = selectedMode;
    setSolving(true);
    setProgress(0);
    workerRef.current.postMessage({ stock, order: adjustedOrder, wasteW, wasteH, priorityOrder });
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-inner">
          <div>
            <h1 className="app-title">Glass Cutting Optimizer</h1>
            <p className="app-subtitle">Minimize waste with guillotine cut planning</p>
          </div>
        </div>
      </header>

      <main className="app-main">
        {/* History */}
        <HistoryPanel
          history={history}
          activeId={activeHistoryId}
          onLoad={(run) => applyRun(run)}
          onDelete={handleDeleteRun}
        />

        {/* Stock + Order */}
        <div className="config-grid">
          <StockPanel stock={stock} setStock={setStock} />
          <OrderPanel order={order} setOrder={setOrder} />
        </div>

        {/* Waste + Optimize */}
        <div className="action-row">
          <div className="waste-config">
            <span className="waste-label">Waste threshold — offcuts smaller than</span>
            <input
              type="number" value={wasteW} min="0.1" step="0.1"
              onChange={(e) => setWasteW(parseFloat(e.target.value) || 0.1)}
              style={{ width: 60 }}
            />
            <span className="waste-label">×</span>
            <input
              type="number" value={wasteH} min="0.1" step="0.1"
              onChange={(e) => setWasteH(parseFloat(e.target.value) || 0.1)}
              style={{ width: 60 }}
            />
            <span className="waste-label">cm are discarded</span>
          </div>

          <div className="order-config">
            <span className="waste-label">Order option</span>
            <select className="priority-select" value={orderMode} onChange={(e) => setOrderMode(e.target.value)}>
              {ORDER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="priority-config">
            <span className="waste-label">Optimize by</span>
            {priorityOrder.map((metric, idx) => (
              <label key={idx} className="priority-item">
                <span className="waste-label">{PRIORITY_LABELS[idx]}</span>
                <select
                  className="priority-select"
                  value={metric}
                  onChange={(e) => updatePriority(idx, e.target.value)}
                >
                  {PRIORITY_METRICS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          <div className="optimize-wrap">
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn-optimize"
                onClick={optimize}
                disabled={stock.length === 0 || order.length === 0 || solving}
              >
                {solving ? `Computing… ${progress}%` : 'Optimize cutting plan'}
              </button>
              {solving && (
                <button className="btn-cancel" onClick={cancelOptimize}>Cancel</button>
              )}
            </div>
            {solving && (
              <div className="progress-track">
                <div className="progress-fill" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
        </div>

        {!hasRun && (
          <div className="hint-box">
            Configure your warehouse stock and order above, then click <strong>Optimize cutting plan</strong> to see the best layout.
          </div>
        )}

        <ResultsPanel result={result} wasteW={wasteW} wasteH={wasteH} />
      </main>

      <footer className="app-footer">
        Glass Cutting Optimizer — Guillotine bin packing algorithm
      </footer>
    </div>
  );
}
